<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" color="primary" variant="plain" elevation="5"
            title="Exposure to different treatment types (any time during follow-up)">
      <v-row no-gutters>
        <v-col>
          <GChart type="PieChart" :data="exposedToSurgeryChartData"
                  :options="exposedToSurgeryChartOptions"
                  v-if="exposedToSurgeryChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
        <v-col>
          <GChart type="PieChart" :data="exposedToRadiotherapyChartData"
                  :options="exposedToRadiotherapyChartOptions"
                  v-if="exposedToRadiotherapyChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
        <v-col>
          <GChart type="PieChart" :data="exposedToChemotherapyChartData"
                  :options="exposedToChemotherapyChartOptions"
                  v-if="exposedToChemotherapyChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
      </v-row>
    </v-card>
    <v-card class="mx-auto" color="primary" variant="plain" elevation="5"
            title="Time from diagnosis to intervention"
            subtitle="Medians and intervals (25%/75% hinges and 1.5 * IQR whiskers)">
      <v-row align="center" no-gutters>
        <v-col cols="8">
          <GChart :settings="{ packages: ['corechart'] }" type="ComboChart"
                  :data="exposedToTreatmentTimeToInterventionChartData"
                  :options="exposedToTreatmentTimeToInterventionChartOptions"
                  v-if="exposedToTreatmentTimeToInterventionChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
        <v-col>
          <transition name="fade" mode="out-in">
            <div class="mr-3" :key="exposedToTreatmentTimeToInterventionData">
              <v-data-table :headers="exposedToTreatmentTimeToInterventionHeaders"
                            :items="exposedToTreatmentTimeToInterventionData"
                            :items-per-page="0">
                <template #bottom></template>
              </v-data-table>
            </div>
          </transition>
        </v-col>
      </v-row>
    </v-card>
    <v-card class="mx-auto" color="primary" variant="plain" elevation="5"
            title="Healthcare resource utilization"
            subtitle="Follow-up year 0 is the same calendar year as diagnosis. Total number of patients in care is shown as N below each follow-up year."
            v-if="(healthcareResourceUtilizationInPatientChartData.length !== 0)
                  || (healthcareResourceUtilizationOutPatientChartData.length !== 0)">
      <v-row align="center" no-gutters>
        <v-col>
          <GChart type="ColumnChart"
                  :data="healthcareResourceUtilizationInPatientChartData"
                  :options="healthcareResourceUtilizationInPatientChartOptions"
                  v-if="healthcareResourceUtilizationInPatientChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
      </v-row>
      <v-row align="center" no-gutters>
        <v-col>
          <GChart type="ColumnChart"
                  :data="healthcareResourceUtilizationOutPatientChartData"
                  :options="healthcareResourceUtilizationOutPatientChartOptions"
                  v-if="healthcareResourceUtilizationOutPatientChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";

export default defineComponent({
  name: 'CancerTreatmentPatternInterventions',
  props: {
    icd: String,
    ageAtDiagnosis: Array,
    diagnosisDate: Array,
    survivalTimeInYears: Array,
    metastasisStatusM: Array,
    metastasisStatusN: Array,
    atcCodes: Array,
    visible: Boolean
  },
  components: {
    GChart
  },
  data: () => ({
    exposedToSurgeryChartData: [],
    exposedToSurgeryChartOptions: {
      title: 'Surgery',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    exposedToRadiotherapyChartData: [],
    exposedToRadiotherapyChartOptions: {
      title: 'Radiotherapy',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    exposedToChemotherapyChartData: [],
    exposedToChemotherapyChartOptions: {
      title: 'Chemotherapy',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    exposedToTreatmentTimeToInterventionChartData: [],
    exposedToTreatmentTimeToInterventionChartOptions: {
      title:'',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      orientation: 'vertical',
      legend: {position: 'none'},
      hAxis: {
        title: 'Time to intervention (months)',
        baselineColor: '#c0c0c0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0'
        },
        minorGridlines: {
          count: 0
        },
        viewWindow: {
          min: -1
        }
      },
      vAxis: {
        baselineColor: '#c0c0c0',
        textStyle: {
          color: '#ffffff'
        }
      },
      lineWidth: 0,
      pointSize: 0,
      intervals: {
        lineWidth: 2,
        barWidth: 1,
        boxWidth: 1,
        style: 'boxes',
        color: '#fdbf6f'
      },
      interval: {
        'lower-whisker': {
          style: 'bars',
          lineWidth: 2
        },
        'lower-hinge': {
          style: 'box',
          lineWidth: 2,
          fillOpacity: 0.5
        },
        'median': {
          style: 'box',
          lineWidth: 2,
          fillOpacity: 0.3
        },
        'upper-hinge': {
          style: 'bars',
          lineWidth: 2
        }
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    exposedToTreatmentTimeToInterventionData: [],
    exposedToTreatmentTimeToInterventionHeaders: [
      {
        title: '',
        key: 'exposed_to_treatment_time_to_intervention_treatment_type'
      },
      {
        title: 'Lower whisker',
        align: 'start',
        key: 'exposed_to_treatment_time_to_intervention_lower_whisker'
      },
      {
        title: 'Lower hinge',
        align: 'start',
        key: 'exposed_to_treatment_time_to_intervention_lower_hinge'
      },
      {
        title: 'Median (months)',
        align: 'start',
        key: 'exposed_to_treatment_time_to_intervention_median'
      },
      {
        title: 'Upper hinge',
        align: 'start',
        key: 'exposed_to_treatment_time_to_intervention_upper_hinge'
      },
      {
        title: 'Upper whisker',
        align: 'start',
        key: 'exposed_to_treatment_time_to_intervention_upper_whisker'
      }
    ],
    healthcareResourceUtilizationInPatientChartData: [],
    healthcareResourceUtilizationInPatientChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      title: 'In-patient care',
      titlePosition: 'out',
      titleTextStyle: {
        color: '#ffffff',
        bold: false
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      legend: {
        position: 'top',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      isStacked: false,
      hAxis: {
        title: 'Follow-up year',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      vAxis: {
        title: 'Number of patients',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        logScale: false
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    },
    healthcareResourceUtilizationOutPatientChartData: [],
    healthcareResourceUtilizationOutPatientChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      title: 'Out-patient care',
      titlePosition: 'out',
      titleTextStyle: {
        color: '#ffffff',
        bold: false
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      legend: {
        position: 'top',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      isStacked: false,
      hAxis: {
        title: 'Follow-up year',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      vAxis: {
        title: 'Number of patients',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        logScale: false
      },
      animation: {
        startup: true,
        duration: 500,
        easing: 'out'
      }
    }
  }),
  methods: {
    async loadPatientsExposedToSurgeryData() {
      return axios.get('cancer-patients-exposed-to-surgery-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadPatientsExposedToRadiotherapyData() {
      return axios.get('cancer-patients-exposed-to-radiotherapy-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadPatientsExposedToChemotherapyData() {
      return axios.get('cancer-patients-exposed-to-chemotherapy-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadPatientsExposedToTreatmentTimeToInterventionData() {
      return axios.get('cancer-patients-exposed-to-treatment-statistics',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadPatientsExposedToTreatmentTimeToInterventionTabularData() {
      return axios.get('cancer-patients-exposed-to-treatment-statistics-tabular',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadHealthcareResourceUtilizationInPatientData() {
      return axios.get('cancer-patients-healthcare-in-patient-resource-utilization',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadHealthcareResourceUtilizationOutPatientData() {
      return axios.get('cancer-patients-healthcare-out-patient-resource-utilization',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadDynamicData() {
      await Promise.all([
        this.loadPatientsExposedToSurgeryData(),
        this.loadPatientsExposedToRadiotherapyData(),
        this.loadPatientsExposedToChemotherapyData(),
        this.loadPatientsExposedToTreatmentTimeToInterventionData(),
        this.loadPatientsExposedToTreatmentTimeToInterventionTabularData(),
        this.loadHealthcareResourceUtilizationInPatientData(),
        this.loadHealthcareResourceUtilizationOutPatientData()
      ]).then(responses => {
        this.exposedToSurgeryChartData = responses[0].data
        this.exposedToRadiotherapyChartData = responses[1].data
        this.exposedToChemotherapyChartData = responses[2].data
        this.exposedToTreatmentTimeToInterventionChartData = responses[3].data
        this.exposedToTreatmentTimeToInterventionData = responses[4].data
        this.healthcareResourceUtilizationInPatientChartData = responses[5].data
        this.healthcareResourceUtilizationOutPatientChartData = responses[6].data
        console.log('Dynamic data loaded CancerTreatmentPatternInterventions')
      })
    }
  },
  mounted() {
    this.loadDynamicData()
  },
  updated() {
    if (this.visible) {
      this.loadDynamicData()
    }
  }
});
</script>
